import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/axios';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import Footer from '../../components/Layout/Footer';
import {
    EnvelopeIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    BuildingOffice2Icon,
    UserIcon,
    PhoneIcon,
    BriefcaseIcon,
    CheckBadgeIcon,
    UserGroupIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const Register = () => {
    const { user } = useAuth();
    const [submitted, setSubmitted] = useState(false);

    const isAdminOrSuperAdmin = Boolean(user && (user.role === 'admin' || user.role === 'super_admin'));

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: '',
        supervisor: '',
        department: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
        queryKey: ['roles', 'public'],
        queryFn: async () => {
            try {
                const response = await api.get('/roles/public');
                return response.data;
            } catch (error) {
                console.warn('Failed to fetch roles from API, using fallback:', error);
                return {
                    success: true,
                    roles: [
                        { _id: 'admin', name: 'admin', description: 'Admin', isActive: true },
                        { _id: 'team_manager', name: 'team_manager', description: 'Sales Manager / Team Manager', isActive: true },
                        { _id: 'sales_executive', name: 'sales_executive', description: 'Sales Executive', isActive: true }
                    ]
                };
            }
        },
        retry: 1,
        staleTime: 5 * 60 * 1000
    });

    const { data: supervisorsData } = useQuery({
        queryKey: ['supervisors'],
        queryFn: async () => {
            const response = await api.get('/users?role=admin,team_manager');
            return response.data;
        },
        enabled: Boolean(isAdminOrSuperAdmin && (formData.role === 'sales_executive' || formData.role === 'team_manager'))
    });

    const roleLabels = {
        admin: 'Admin',
        team_manager: 'Sales Manager / Team Manager',
        sales_executive: 'Sales Executive'
    };
    const allowedRoles = (rolesData?.roles || [])
        .filter(role => role.isActive && ['admin', 'team_manager', 'sales_executive'].includes(role.name))
        .map(role => ({
            value: role.name,
            label: role.description || roleLabels[role.name] || role.name,
            roleId: role._id
        }));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'role') {
            setFormData(prev => ({
                ...prev,
                supervisor: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.phone) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9]{10}$/.test(formData.phone)) {
            newErrors.phone = 'Phone must be 10 digits';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!formData.role) {
            newErrors.role = 'Role is required';
        }

        if (isAdminOrSuperAdmin) {
            if (formData.role === 'sales_executive' && !formData.supervisor) {
                newErrors.supervisor = 'Supervisor is required for Sales Executive';
            }
            if (formData.role === 'sales_executive' && !formData.department.trim()) {
                newErrors.department = 'Department is required for Sales Executive';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const selectedRoleObj = allowedRoles.find(r => r.value === formData.role);
            const roleId = selectedRoleObj?.roleId;

            const registrationData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                password: formData.password,
                role: isAdminOrSuperAdmin ? formData.role : 'sales_executive',
                roleId: roleId || undefined,
                supervisor: formData.supervisor || undefined,
                department: formData.department.trim() || undefined
            };

            const response = await api.post('/auth/register-user', registrationData);

            if (response.data.success) {
                setSubmitted(true);
                toast.success('Registration submitted! Your account is pending Super Admin approval.');
            }
        } catch (error) {
            console.error('Registration error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to register user. Please try again.';
            toast.error(errorMessage);

            if (error.response?.data?.errors) {
                const fieldErrors = {};
                error.response.data.errors.forEach(err => {
                    if (err.param) {
                        fieldErrors[err.param] = err.msg;
                    }
                });
                setErrors(prev => ({ ...prev, ...fieldErrors }));
            }
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex flex-col justify-between bg-slate-100/80 font-sans">
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md w-full glass-card p-8 rounded-3xl shadow-xl text-center space-y-6 border border-slate-200">
                        <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 text-amber-600 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                            <CheckBadgeIcon className="w-8 h-8" />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                                Registration Submitted
                            </h2>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Your account registration has been received and queued for Super Admin verification.
                            </p>
                        </div>

                        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left text-xs text-amber-800 space-y-1">
                            <span className="font-bold block">Status: Pending Verification</span>
                            <span>You will receive access approval as soon as the administrator verifies your profile details.</span>
                        </div>

                        <Link
                            to="/login"
                            className="btn btn-primary w-full py-3 text-xs font-extrabold"
                        >
                            Return to Login
                        </Link>
                    </div>
                </div>
                <Footer variant="app" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col justify-between bg-slate-100/80 font-sans">
            <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
                <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-12 rounded-3xl overflow-hidden shadow-2xl border border-slate-200/80 bg-white">
                    {/* Left Brand Showcase Side */}
                    <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-8 text-white flex flex-col justify-between relative overflow-hidden">
                        <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-lg text-white font-extrabold">
                                    <BuildingOffice2Icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <span className="text-xl font-extrabold text-white">Book<span className="text-indigo-400">My</span>Acre</span>
                                    <p className="text-[10px] text-indigo-300 font-bold uppercase tracking-wider">Enterprise Onboarding</p>
                                </div>
                            </div>

                            <div className="space-y-3 pt-4">
                                <h2 className="text-2xl font-extrabold text-white leading-snug">
                                    Join the Enterprise Real Estate Network
                                </h2>
                                <p className="text-xs text-slate-300 leading-relaxed">
                                    {isAdminOrSuperAdmin
                                        ? 'Register sales managers or sales executives with full role control and supervisor mapping.'
                                        : 'Request account onboarding for land sales, customer CRM management, and site visit operations.'
                                    }
                                </p>
                            </div>

                            <div className="space-y-3 pt-2">
                                <div className="p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/60 flex items-start gap-3">
                                    <UserGroupIcon className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-white">Role-Based Access</h4>
                                        <p className="text-[11px] text-slate-400">Customized sales dashboards for executives, managers & admins.</p>
                                    </div>
                                </div>

                                <div className="p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/60 flex items-start gap-3">
                                    <CheckBadgeIcon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-white">Secure Approval Queue</h4>
                                        <p className="text-[11px] text-slate-400">Super Admin authorization protects organizational customer data.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative z-10 pt-6 border-t border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
                            <span>Already registered?</span>
                            <Link to="/login" className="text-indigo-400 font-bold hover:underline flex items-center gap-1">
                                Sign In <ArrowRightIcon className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>

                    {/* Right Form Card */}
                    <div className="lg:col-span-7 p-6 sm:p-10 bg-slate-50/60 flex flex-col justify-center">
                        <div className="mb-6">
                            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                                {isAdminOrSuperAdmin ? 'Register New System User' : 'Create Account'}
                            </h2>
                            <p className="text-xs text-slate-500 mt-1">
                                {isAdminOrSuperAdmin
                                    ? 'Assign user role, phone credentials and supervisor mapping.'
                                    : 'Fill out your profile information to request account access.'
                                }
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* First Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">First Name *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <UserIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="firstName"
                                            type="text"
                                            required
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon text-xs ${errors.firstName ? 'border-rose-400' : ''}`}
                                            placeholder="John"
                                        />
                                    </div>
                                    {errors.firstName && <p className="text-[11px] text-rose-600 mt-0.5">{errors.firstName}</p>}
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Last Name *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <UserIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="lastName"
                                            type="text"
                                            required
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon text-xs ${errors.lastName ? 'border-rose-400' : ''}`}
                                            placeholder="Doe"
                                        />
                                    </div>
                                    {errors.lastName && <p className="text-[11px] text-rose-600 mt-0.5">{errors.lastName}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Email Address *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <EnvelopeIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="email"
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon text-xs ${errors.email ? 'border-rose-400' : ''}`}
                                            placeholder="name@company.com"
                                        />
                                    </div>
                                    {errors.email && <p className="text-[11px] text-rose-600 mt-0.5">{errors.email}</p>}
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Phone Number *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <PhoneIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="phone"
                                            type="tel"
                                            required
                                            maxLength="10"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon text-xs ${errors.phone ? 'border-rose-400' : ''}`}
                                            placeholder="10 digit phone number"
                                        />
                                    </div>
                                    {errors.phone && <p className="text-[11px] text-rose-600 mt-0.5">{errors.phone}</p>}
                                </div>

                                {/* Role */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Role *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <BriefcaseIcon className="h-4 w-4" />
                                        </div>
                                        <select
                                            name="role"
                                            required
                                            value={formData.role}
                                            onChange={handleChange}
                                            disabled={rolesLoading}
                                            className={`form-select input-has-left-icon text-xs ${errors.role ? 'border-rose-400' : ''}`}
                                        >
                                            <option value="">{rolesLoading ? 'Loading roles...' : 'Select a role'}</option>
                                            {allowedRoles.length > 0 ? (
                                                allowedRoles.map(role => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))
                                            ) : (
                                                <>
                                                    <option value="team_manager">Sales Manager / Team Manager</option>
                                                    <option value="sales_executive">Sales Executive</option>
                                                </>
                                            )}
                                        </select>
                                    </div>
                                    {errors.role && <p className="text-[11px] text-rose-600 mt-0.5">{errors.role}</p>}
                                </div>

                                {/* Supervisor (conditional) */}
                                {isAdminOrSuperAdmin && (formData.role === 'sales_executive' || formData.role === 'team_manager') && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Supervisor {formData.role === 'sales_executive' ? '*' : ''}
                                        </label>
                                        <select
                                            name="supervisor"
                                            required={formData.role === 'sales_executive'}
                                            value={formData.supervisor}
                                            onChange={handleChange}
                                            className={`form-select text-xs ${errors.supervisor ? 'border-rose-400' : ''}`}
                                        >
                                            <option value="">Select supervisor</option>
                                            {supervisorsData?.users?.map(supervisor => (
                                                <option key={supervisor._id} value={supervisor._id}>
                                                    {supervisor.firstName} {supervisor.lastName} ({supervisor.role})
                                                </option>
                                            ))}
                                        </select>
                                        {errors.supervisor && <p className="text-[11px] text-rose-600 mt-0.5">{errors.supervisor}</p>}
                                    </div>
                                )}

                                {/* Department (conditional) */}
                                {isAdminOrSuperAdmin && formData.role === 'sales_executive' && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Department *</label>
                                        <input
                                            name="department"
                                            type="text"
                                            required
                                            value={formData.department}
                                            onChange={handleChange}
                                            className={`form-input text-xs ${errors.department ? 'border-rose-400' : ''}`}
                                            placeholder="Sales"
                                        />
                                        {errors.department && <p className="text-[11px] text-rose-600 mt-0.5">{errors.department}</p>}
                                    </div>
                                )}

                                {/* Password */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Password *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <LockClosedIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            value={formData.password}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon input-has-right-icon text-xs ${errors.password ? 'border-rose-400' : ''}`}
                                            placeholder="Min 6 characters"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="text-[11px] text-rose-600 mt-0.5">{errors.password}</p>}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Confirm Password *</label>
                                    <div className="relative rounded-xl shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                            <LockClosedIcon className="h-4 w-4" />
                                        </div>
                                        <input
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            required
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className={`form-input input-has-left-icon input-has-right-icon text-xs ${errors.confirmPassword ? 'border-rose-400' : ''}`}
                                            placeholder="Repeat password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                                        >
                                            {showConfirmPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && <p className="text-[11px] text-rose-600 mt-0.5">{errors.confirmPassword}</p>}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary w-full py-3 text-xs font-extrabold mt-4"
                            >
                                {loading ? <LoadingSpinner size="small" /> : (isAdminOrSuperAdmin ? 'Register User' : 'Submit Registration')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <Footer variant="app" />
        </div>
    );
};

export default Register;
